/**
 * ThemeContext.jsx
 *
 * Gère le mode nuit/jour de l'application
 * - Par défaut : DARK MODE
 * - Stocké en localStorage
 * - Utilisable via useTheme() hook
 */

import { createContext, useState, useContext, useEffect } from "react";

const ThemeContext = createContext();

/**
 * ThemeProvider - Wrapper pour fournir le thème à toute l'app
 * @param {ReactNode} children - Composants enfants
 */
export function ThemeProvider({ children }) {
    // Récupère le mode sauvegardé ou utilise DARK par défaut
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem("theme-mode");
        return saved ? saved === "dark" : true; // ✅ DARK MODE PAR DÉFAUT
    });

    // Sauvegarde le choix en localStorage
    useEffect(() => {
        localStorage.setItem("theme-mode", isDarkMode ? "dark" : "light");
    }, [isDarkMode]);

    const toggleTheme = () => {
        setIsDarkMode(prev => !prev);
    };

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

/**
 * Hook pour accéder au contexte du thème
 * @returns {Object} { isDarkMode, toggleTheme }
 */
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within ThemeProvider");
    }
    return context;
};